<template>
  <div class="ap-portal">
    <Transition name="ap-fade">
      <div
        v-if="value"
        class="ap-popup"
        :style="{
          left:   pos.x + 'px',
          top:    pos.y + 'px',
          width:  size.w + 'px',
          height: size.h + 'px',
        }"
        ref="popupEl"
      >

        <!-- ══ HEADER ══════════════════════════════════════════════ -->
        <div class="ap-header" @mousedown="startDrag">
          <div class="ap-header__left">
            <div class="ap-header__icon">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/>
                <polyline points="9 22 9 12 15 12 15 22"/>
              </svg>
            </div>
            <span class="ap-header__title">Alojamientos</span>
            <span v-if="meta" class="ap-header__count">{{ meta.total }}</span>
          </div>
          <button class="ap-close" @click="$emit('input', false)">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <!-- ══ SEARCH ═══════════════════════════════════════════════ -->
        <div class="ap-search-wrap">
          <svg class="ap-search-icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8">
            <circle cx="6.5" cy="6.5" r="5"/><path d="M10.5 10.5l3.5 3.5" stroke-linecap="round"/>
          </svg>
          <input
            class="ap-search"
            v-model="query"
            placeholder="Nombre, dirección, ID…"
            @input="onInput"
            ref="searchEl"
          />
          <Transition name="ap-fade-q">
            <button v-if="query" class="ap-search-clear" @click="query = ''; doFetch()">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </Transition>
        </div>

        <!-- ══ BODY ══════════════════════════════════════════════════ -->
        <div class="ap-body">
          <Transition name="ap-slide" mode="out-in">

            <!-- ── DETALLE ──────────────────────────────────────── -->
            <div v-if="detail" key="detail" class="ap-detail-wrap">

              <button class="ap-back" @click="detail = null">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <polyline points="15 18 9 12 15 6"/>
                </svg>
                Volver a la lista
              </button>

              <div class="ap-detail">

                <!-- Hero -->
                <div class="ap-detail-hero">
                  <div class="ap-detail-hero__emoji">🏠</div>
                  <div class="ap-detail-hero__content">
                    <div class="ap-detail-hero__name">{{ localeName(detail.nombre) }}</div>
                    <div class="ap-detail-hero__addr">
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/><circle cx="12" cy="10" r="3"/>
                      </svg>
                      {{ detail.direccion }}
                    </div>
                  </div>
                  <div class="ap-detail-hero__id">#{{ detail.idalojamiento }}</div>
                </div>

                <!-- Badges -->
                <div class="ap-badge-row">
                  <span class="ap-badge ap-badge--tipo">{{ detail.tipo }}</span>
                  <span class="ap-badge ap-badge--mod">{{ detail.modalidad_tiempo }}</span>
                  <span v-if="detail.aprobado"   class="ap-badge ap-badge--ok">✓ Aprobado</span>
                  <span v-if="detail.verificado" class="ap-badge ap-badge--ok">✓ Verificado</span>
                  <span v-if="detail.destacado"  class="ap-badge ap-badge--star">★ Destacado</span>
                </div>

                <!-- Stats 2×2 (se muestra "Cantidad mínima para alquilar" en lugar de Camas) -->
                <div class="ap-stats-grid">
                  <div class="ap-stat-card">
                    <span class="ap-stat-card__val">{{ (detail && detail.m2) ? detail.m2 : '—' }}</span>
                    <span class="ap-stat-card__lbl">m²</span>
                  </div>
                  <div class="ap-stat-card">
                    <span class="ap-stat-card__val">{{ (detail && detail.maximo_huespedes) ? detail.maximo_huespedes : '—' }}</span>
                    <span class="ap-stat-card__lbl">Huéspedes</span>
                  </div>
                  <div class="ap-stat-card">
                    <span class="ap-stat-card__val">{{ (detail && detail.tiempo_min) ? detail.tiempo_min : '—' }}</span>
                    <span class="ap-stat-card__lbl">Días mín.</span>
                  </div>
                  <!-- removed 'Cantidad mínima para alquilar' as requested -->
                </div>

                <!-- Precio -->
                <div class="ap-precio">
                  <div class="ap-precio__label">PRECIO</div>
                  <div class="ap-precio__val">{{ formatPrecio(detail) }}</div>
                </div>

                <!-- Host -->
                <div v-if="detail.anfitrion" class="ap-section ap-section--host-below-price">
                  <div class="ap-section__label">HOST</div>
                  <div class="ap-host">
                    <div class="ap-host__avatar">
                      {{ initials(detail.anfitrion.nombre, detail.anfitrion.apellido) }}
                    </div>
                    <div class="ap-host__info">
                      <div class="ap-host__name">{{ detail.anfitrion.nombre }} {{ detail.anfitrion.apellido }}</div>
                      <div class="ap-host__meta">{{ detail.anfitrion.correo }}</div>
                      <div class="ap-host__meta">{{ detail.anfitrion.telefono }}</div>
                    </div>
                  </div>
                </div>

                <!-- Amenidades -->
                <div v-if="amenidades(detail).length" class="ap-section">
                  <div class="ap-section__label">AMENIDADES</div>
                  <div class="ap-amenidades">
                    <div v-for="a in amenidades(detail)" :key="a.key" class="ap-amen">
                      <span class="ap-amen__icon">{{ a.icon }}</span>
                      <span class="ap-amen__label">{{ a.label }}</span>
                    </div>
                  </div>
                </div>

              </div>
            </div>

            <!-- ── LISTA ────────────────────────────────────────── -->
            <div v-else key="list" class="ap-list-wrap">

              <div v-if="loading" class="ap-state">
                <span class="ap-spinner"></span>
                <span>Buscando…</span>
              </div>

              <div v-else-if="error" class="ap-state ap-state--error">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                {{ error }}
              </div>

              <div v-else-if="items.length === 0" class="ap-state">
                <span style="font-size:1.8rem;opacity:.2">🏚️</span>
                Sin resultados
              </div>

              <div v-else class="ap-list">
                <div
                  v-for="(a, i) in items"
                  :key="a.idalojamiento"
                  class="ap-item"
                  :class="{ 'ap-item--star': a.destacado }"
                  :style="{ animationDelay: i * 35 + 'ms' }"
                  @click="openDetail(a.idalojamiento)"
                >
                  <div class="ap-item__stripe"></div>

                  <div class="ap-item__icon">
                    <span>🏠</span>
                    <span v-if="a.destacado" class="ap-item__star">★</span>
                  </div>

                  <div class="ap-item__body">
                    <div class="ap-item__top">
                      <span class="ap-item__name">{{ localeName(a.nombre) }}</span>
                      <span class="ap-item__id">#{{ a.idalojamiento }}</span>
                    </div>
                    <div class="ap-item__addr">{{ a.direccion }}</div>
                    <div class="ap-item__row3">
                      <span class="ap-badge ap-badge--tipo ap-badge--xs">{{ a.tipo }}</span>
                      <span class="ap-badge ap-badge--mod ap-badge--xs">{{ a.modalidad_tiempo }}</span>
                      <span class="ap-item__precio">{{ formatPrecio(a) }}</span>
                    </div>
                  </div>

                  <svg class="ap-item__arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </div>
              </div>

              <div v-if="meta && meta.last_page > 1" class="ap-pagination">
                <button :disabled="meta.current_page === 1" @click="changePage(meta.current_page - 1)">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="15 18 9 12 15 6"/></svg>
                </button>
                <span>{{ meta.current_page }} <span class="ap-pag-sep">/</span> {{ meta.last_page }}</span>
                <button :disabled="meta.current_page === meta.last_page" @click="changePage(meta.current_page + 1)">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="9 18 15 12 9 6"/></svg>
                </button>
              </div>

            </div>
          </Transition>
        </div>

        <!-- ══ RESIZE HANDLES ════════════════════════════════════════ -->
        <!-- Bordes -->
        <div class="ap-resize ap-resize--e"  @mousedown.stop="startResize($event, 'e')"></div>
        <div class="ap-resize ap-resize--s"  @mousedown.stop="startResize($event, 's')"></div>
        <div class="ap-resize ap-resize--w"  @mousedown.stop="startResize($event, 'w')"></div>
        <div class="ap-resize ap-resize--n"  @mousedown.stop="startResize($event, 'n')"></div>
        <!-- Esquinas -->
        <div class="ap-resize ap-resize--se" @mousedown.stop="startResize($event, 'se')"></div>
        <div class="ap-resize ap-resize--sw" @mousedown.stop="startResize($event, 'sw')"></div>
        <div class="ap-resize ap-resize--ne" @mousedown.stop="startResize($event, 'ne')"></div>
        <div class="ap-resize ap-resize--nw" @mousedown.stop="startResize($event, 'nw')"></div>

      </div>
    </Transition>
  </div>
</template>

<script>
import apiUrls from '@/new-constants/apiUrls'
import { localeText as localeTextUtil, priceConverter } from '@/Utils/Utils'
export default {
  name: 'AlojamientosPopup',
  props: {
    value: { type: Boolean, default: false },
    baseUrl:    { type: String,  default: apiUrls.base }
  },
  data() {
    return {
      items: [],
      meta: null,
      loading: false,
      error: null,
      query: '',
      page: 1,
      detail: null,
      size: { w: 480, h: 560 },
      pos: { x: 0, y: 0 },
      _dragging: false,
      _ox: 0,
      _oy: 0,
      _resizing: false,
      _debTimer: null
    }
  },
  methods: {
    MIN_W() { return 320 },
    MIN_H() { return 300 },
    MAX_W() { return window.innerWidth - 16 },
    MAX_H() { return window.innerHeight - 16 },
    clampPos(x, y, w, h) { const vw = window.innerWidth; const vh = window.innerHeight; return { x: Math.min(Math.max(x,0), vw - w), y: Math.min(Math.max(y,0), vh - h) } },
    startDrag(e) {
      if (e.target.closest('button, .ap-resize')) return
      this._dragging = true
      this._ox = e.clientX - this.pos.x
      this._oy = e.clientY - this.pos.y
      document.addEventListener('mousemove', this._onDrag)
      document.addEventListener('mouseup', this._stopDrag)
    },
    _onDrag(e) { if (!this._dragging) return; const clamped = this.clampPos(e.clientX - this._ox, e.clientY - this._oy, this.size.w, this.size.h); this.pos = clamped },
    _stopDrag() { this._dragging = false; document.removeEventListener('mousemove', this._onDrag); document.removeEventListener('mouseup', this._stopDrag) },
    startResize(e, dir) {
      e.preventDefault(); this._resizing = true
      const startX = e.clientX, startY = e.clientY
      const startW = this.size.w, startH = this.size.h
      const startPX = this.pos.x, startPY = this.pos.y
      const onMove = (ev) => {
        if (!this._resizing) return
        const dx = ev.clientX - startX, dy = ev.clientY - startY
        let newW = startW, newH = startH, newX = startPX, newY = startPY
        if (dir.includes('e')) newW = startW + dx
        if (dir.includes('w')) { newW = startW - dx; newX = startPX + dx }
        if (dir.includes('s')) newH = startH + dy
        if (dir.includes('n')) { newH = startH - dy; newY = startPY + dy }
        newW = Math.min(Math.max(newW, this.MIN_W()), this.MAX_W())
        newH = Math.min(Math.max(newH, this.MIN_H()), this.MAX_H())
        if (dir.includes('w')) newX = startPX + startW - newW
        if (dir.includes('n')) newY = startPY + startH - newH
        const clamped = this.clampPos(newX, newY, newW, newH)
        this.size = { w: newW, h: newH }
        this.pos = clamped
      }
      const onUp = () => { this._resizing = false; document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp) }
      document.addEventListener('mousemove', onMove); document.addEventListener('mouseup', onUp)
    },
    onInput() {
      clearTimeout(this._debTimer)
      this._debTimer = setTimeout(() => {
        this.page = 1
        const q = this.query.trim()
        if (q && /^\d+$/.test(q)) {
          this.openDetail(Number(q))
        } else {
          this.doFetch()
        }
      }, 300)
    },
    async doFetch() {
      this.loading = true; this.error = null
      try {
        const params = new URLSearchParams({ page: this.page, limit: 15 })
        if (this.query && this.query.trim()) params.set('q', this.query.trim())
        const res = await window.fetch(`${apiUrls.adminAlojamientos}?${params}`)
        const json = await res.json()
        this.items = json.data ? json.data : []
        this.meta  = json.meta ? json.meta : null
      } catch (e) { this.error = 'No se pudo conectar.' }
      finally { this.loading = false }
    },
    async openDetail(id) { this.loading = true; try { const res = await window.fetch(`${apiUrls.adminAlojamientos}/${id}`); const json = await res.json(); this.detail = json.data } catch (e) { this.error = 'Error cargando detalle.' } finally { this.loading = false } },
    changePage(p) { this.page = p; this.doFetch() },
    initials(n, a) { const first = (n && n[0]) ? n[0] : ''; const last = (a && a[0]) ? a[0] : ''; return ((first + last).toUpperCase()) || '?' },
    formatPrecio(a) {
      if (!a) return '—'

      const fmt = (v) => {
        if (v === null || v === undefined || Number.isNaN(Number(v))) return '—'
        return Number(v).toLocaleString('es-AR', { maximumFractionDigits: 0 })
      }

      // Helper to attempt conversion using priceConverter; falls back to original value/currency
      const tryConvert = (amount, currency) => {
        try {
          const converted = priceConverter({ amount: amount, currency: currency })
          if (!converted || !converted.amount || Number.isNaN(Number(converted.amount))) {
            return { amount, currency }
          }
          return converted
        } catch (e) {
          return { amount, currency }
        }
      }

      const parts = []

      if (a.arriendo !== null && a.arriendo !== undefined) {
        const v = tryConvert(a.arriendo, a.moneda || 'ARS')
        parts.push(`${v.currency} ${fmt(v.amount)}/mes`)
      }
      if (a.arriendo_diario !== null && a.arriendo_diario !== undefined) {
        const v2 = tryConvert(a.arriendo_diario, a.moneda || 'ARS')
        parts.push(`${v2.currency} ${fmt(v2.amount)}/día`)
      }

      return parts.length ? parts.join(' · ') : '—'
    },
    localeName(v) { return localeTextUtil(v, (this.$i18n && this.$i18n.locale) ? this.$i18n.locale : 'es') },
    amenidades(a) { return [ { key:'wifi', icon:'📶', label:'Wifi', val: a && a.wifi }, { key:'tv', icon:'📺', label:'TV', val: a && a.tv }, { key:'cocina', icon:'🍳', label:'Cocina', val: a && a.cocina }, { key:'lavadora', icon:'🫧', label:'Lavadora', val: a && a.lavadora }, { key:'ac', icon:'❄️', label:'A/C', val: a && a.ac }, { key:'piscina', icon:'🏊', label:'Piscina', val: a && a.piscina }, { key:'mascotas', icon:'🐾', label:'Mascotas', val: a && a.mascotas }, { key:'metro', icon:'🚇', label:'Metro', val: a && a.estacion_metro }, { key:'gym', icon:'🏋️', label:'Gimnasio', val: a && a.gimnasio_interno }, { key:'estac', icon:'🚗', label:'Estacionamiento', val: a && a.estacionamiento_privado }, { key:'calefaccion', icon:'🔥', label:'Calefacción', val: a && a.calefaccion }, { key:'escritorio', icon:'💻', label:'Escritorio', val: a && a.escritorio } ].filter(x => x.val) }
  },
  watch: {
    value(v) {
      if (v) {
        this.detail = null; this.query = ''; this.page = 1
        this.pos = this.clampPos(window.innerWidth / 2 - this.size.w / 2, window.innerHeight / 2 - this.size.h / 2, this.size.w, this.size.h)
        this.doFetch()
        this.$nextTick(() => { if (this.$refs.searchEl) this.$refs.searchEl.focus() })
      }
    }
  }
}
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700;800;900&family=Geist+Mono:wght@400;500;600&family=Instrument+Serif:ital@0;1&display=swap');
/* ─── TOKENS ────────────────────────────────────── */
.ap-popup {
  --primary:        #FC7010;
  --primary-light:  rgba(252,112,16,0.10);
  --primary-dim:    rgba(252,112,16,0.22);
  --primary-dark:   #B84800;
  --bg:             #ffffff;
  --bg-1:           #FAFAF9;
  --bg-2:           #F4F3F0;
  --border:         #ECEAE4;
  --border-str:     #DBD9D2;
  --txt:            #0A0A0B;
  --txt-2:          #383840;
  --txt-3:          #6E6E76;
  --txt-4:          #A0A0A8;
  --red:            #dc2626;
  --green:          #059669;
  --green-bg:       #f0fdf4;
  --green-bdr:      #a7f3d0;
  --indigo:         #4338ca;
  --indigo-light:   rgba(67,56,202,0.08);
  --indigo-dim:     rgba(67,56,202,0.20);
  --star:           #d97706;
  --star-light:     rgba(252,112,16,0.10);
  --star-dim:       rgba(252,112,16,0.22);
}

/* ══ POPUP ══════════════════════════════════════════ */
.ap-popup {
  position: fixed;
  z-index: 1000;
  background: var(--bg);
  border: 1px solid var(--border-str);
  border-radius: 16px;
  box-shadow:
    0 4px 6px rgba(0,0,0,0.04),
    0 12px 32px rgba(0,0,0,0.10),
    0 32px 64px rgba(0,0,0,0.08);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: 'Geist', 'DM Sans', 'Segoe UI', system-ui, sans-serif;
  font-size: 13px;
  color: var(--txt);
}

/* ══ RESIZE HANDLES ════════════════════════════════ */
.ap-resize { position: absolute; z-index: 10; }
.ap-resize--e  { right: 0;  top: 8px;  bottom: 8px; width: 6px;  cursor: ew-resize; }
.ap-resize--w  { left: 0;   top: 8px;  bottom: 8px; width: 6px;  cursor: ew-resize; }
.ap-resize--s  { bottom: 0; left: 8px; right: 8px;  height: 6px; cursor: ns-resize; }
.ap-resize--n  { top: 0;    left: 8px; right: 8px;  height: 6px; cursor: ns-resize; }
.ap-resize--se { right: 0;  bottom: 0; width: 12px; height: 12px; cursor: nwse-resize; }
.ap-resize--sw { left: 0;   bottom: 0; width: 12px; height: 12px; cursor: nesw-resize; }
.ap-resize--ne { right: 0;  top: 0;    width: 12px; height: 12px; cursor: nesw-resize; }
.ap-resize--nw { left: 0;   top: 0;    width: 12px; height: 12px; cursor: nwse-resize; }

/* ══ HEADER ════════════════════════════════════════ */
.ap-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 14px;
  background: #ffffff;
  border-bottom: 1px solid var(--border);
  cursor: grab;
  user-select: none;
  flex-shrink: 0;
  position: relative;
}
.ap-header::after {
  content: '';
  position: absolute;
  bottom: 0; left: 0; right: 0;
  height: 1px;
  background: linear-gradient(90deg, var(--primary) 0%, transparent 60%);
}
.ap-header:active { cursor: grabbing; }
.ap-header__left { display: flex; align-items: center; gap: 9px; }
.ap-header__icon {
  width: 30px;
  height: 30px;
  border-radius: 9px;
  background: var(--primary-light);
  border: 1px solid var(--primary-dim);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-dark);
  flex-shrink: 0;
}
.ap-header__title {
  font-weight: 800;
  font-size: 14px;
  color: var(--txt);
  letter-spacing: -0.2px;
}
.ap-header__count {
  font-size: 10.5px;
  font-weight: 700;
  color: var(--txt-3);
  background: #F4F3F0;
  border: 1px solid var(--border-str);
  padding: 2px 8px;
  border-radius: 20px;
}
.ap-close {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #FAFAF9;
  border: 1px solid var(--border-str);
  cursor: pointer;
  color: var(--txt-4);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}
.ap-close:hover {
  background: #fef2f2;
  border-color: #fecaca;
  color: var(--red);
}

/* ══ SEARCH ════════════════════════════════════════ */
.ap-search-wrap {
  position: relative;
  padding: 10px 12px;
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
  background: var(--bg-1);
}
.ap-search-icon {
  position: absolute;
  left: 22px;
  top: 50%;
  transform: translateY(-50%);
  width: 12px;
  height: 12px;
  color: var(--txt-4);
  pointer-events: none;
}
.ap-search {
  width: 100%;
  background: #ffffff;
  border: 1px solid var(--border-str);
  border-radius: 9px;
  color: var(--txt);
  font-size: 12.5px;
  font-family: inherit;
  padding: 8px 28px 8px 28px;
  outline: none;
  box-sizing: border-box;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.ap-search::placeholder { color: var(--txt-4); }
.ap-search:focus {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px rgba(252,112,16,0.12);
}
.ap-search-clear {
  position: absolute;
  right: 22px;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  border-radius: 5px;
  background: #F4F3F0;
  border: none;
  cursor: pointer;
  color: var(--txt-3);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.12s;
}
.ap-search-clear:hover {
  background: var(--primary-light);
  color: var(--primary-dark);
}

/* ══ BODY ══════════════════════════════════════════ */
.ap-body {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.ap-list-wrap,
.ap-detail-wrap {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

/* ══ LISTA ═════════════════════════════════════════ */
.ap-list { flex: 1; overflow-y: auto; }

.ap-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 14px;
  border-bottom: 1px solid var(--border);
  cursor: pointer;
  transition: background 0.15s ease, transform 0.1s ease;
  animation: ap-item-in 0.2s ease both;
  position: relative;
}
@keyframes ap-item-in {
  from { opacity: 0; transform: translateY(5px); }
  to   { opacity: 1; transform: translateY(0); }
}
.ap-item:hover {
  background: #FAFAF9;
  transform: translateX(1px);
}
.ap-item:hover .ap-item__arrow {
  opacity: 1;
  transform: translateX(0);
}
.ap-item:hover .ap-item__stripe { opacity: 1; }
.ap-item--star .ap-item__stripe { opacity: 0.4; }

.ap-item__stripe {
  position: absolute;
  left: 0;
  top: 8px;
  bottom: 8px;
  width: 3px;
  border-radius: 0 3px 3px 0;
  background: var(--primary);
  opacity: 0;
  transition: opacity 0.15s;
}

.ap-item__icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  flex-shrink: 0;
  background: var(--primary-light);
  border: 1px solid var(--primary-dim);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  position: relative;
}
.ap-item__star {
  position: absolute;
  top: -5px;
  right: -5px;
  width: 14px;
  height: 14px;
  border-radius: 99px;
  background: var(--star);
  color: #ffffff;
  font-size: 9px;
  font-weight: 900;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ap-item__body  { flex: 1; min-width: 0; }
.ap-item__top   { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 2px; }
.ap-item__name  { font-weight: 700; font-size: 13px; color: var(--txt); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 220px; }
.ap-item__id    { font-size: 10.5px; color: var(--txt-4); flex-shrink: 0; margin-left: 6px; }
.ap-item__addr  { font-size: 11.5px; color: var(--txt-3); margin-bottom: 5px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ap-item__row3  { display: flex; align-items: center; gap: 4px; flex-wrap: wrap; }
.ap-item__precio { font-size: 10.5px; color: var(--txt-3); margin-left: auto; font-weight: 600; }
.ap-item__arrow {
  color: var(--txt-4);
  flex-shrink: 0;
  opacity: 0;
  transform: translateX(-4px);
  transition: all 0.15s ease;
}

/* ══ BADGES ═════════════════════════════════════════ */
.ap-badge {
  display: inline-flex;
  align-items: center;
  font-size: 10.5px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 20px;
}
.ap-badge--xs { font-size: 10px; padding: 1px 6px; }
.ap-badge--tipo  { background: var(--indigo-light); color: var(--indigo); border: 1px solid var(--indigo-dim); }
.ap-badge--mod   { background: #F4F3F0; color: var(--txt-3); border: 1px solid var(--border-str); }
.ap-badge--ok    { background: var(--green-bg); color: var(--green); border: 1px solid var(--green-bdr); }
.ap-badge--star  { background: var(--star-light); color: var(--star); border: 1px solid var(--star-dim); }
.ap-badge-row    { display: flex; gap: 5px; flex-wrap: wrap; }

/* ══ PAGINACIÓN ════════════════════════════════════ */
.ap-pagination {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 9px;
  border-top: 1px solid var(--border);
  font-size: 12px;
  color: var(--txt-3);
  flex-shrink: 0;
  background: var(--bg-1);
}
.ap-pag-sep { opacity: 0.4; margin: 0 2px; }
.ap-pagination button {
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid var(--border-str);
  color: var(--txt-3);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s ease;
}
.ap-pagination button:hover:not(:disabled) {
  border-color: var(--primary-dim);
  color: var(--primary-dark);
  background: var(--primary-light);
}
.ap-pagination button:disabled { opacity: 0.3; cursor: not-allowed; }

/* ══ DETALLE ═══════════════════════════════════════ */
.ap-back {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 9px 14px;
  font-size: 12px;
  font-weight: 700;
  color: var(--primary-dark);
  background: var(--bg-1);
  border: none;
  border-bottom: 1px solid var(--border);
  cursor: pointer;
  width: 100%;
  text-align: left;
  transition: background 0.15s;
  font-family: inherit;
  flex-shrink: 0;
}
.ap-back:hover { background: var(--primary-light); }

.ap-detail {
  flex: 1;
  overflow-y: auto;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Hero del detalle */
.ap-detail-hero {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px;
  background: var(--bg-1);
  border: 1px solid var(--border-str);
  border-radius: 12px;
  position: relative;
  overflow: hidden;
}
.ap-detail-hero::before {
  content: '';
  position: absolute;
  top: 0; left: 0; right: 0;
  height: 3px;
  background: linear-gradient(90deg, var(--primary) 0%, transparent 65%);
  border-radius: 12px 12px 0 0;
}
.ap-detail-hero__emoji {
  width: 46px;
  height: 46px;
  border-radius: 11px;
  flex-shrink: 0;
  background: var(--primary-light);
  border: 1px solid var(--primary-dim);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
}
.ap-detail-hero__content { flex: 1; min-width: 0; }
.ap-detail-hero__name {
  font-size: 15px;
  font-weight: 800;
  color: var(--txt);
  margin-bottom: 5px;
  letter-spacing: -0.2px;
}
.ap-detail-hero__addr {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11.5px;
  color: var(--txt-3);
}
.ap-detail-hero__id {
  font-size: 10.5px;
  font-weight: 700;
  color: var(--txt-4);
  background: var(--bg-2);
  border: 1px solid var(--border-str);
  padding: 3px 8px;
  border-radius: 6px;
  flex-shrink: 0;
  align-self: flex-start;
}

/* Stats grid 2×2 */
.ap-stats-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 7px;
}
.ap-stat-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  padding: 10px 6px;
  background: var(--bg-1);
  border: 1px solid var(--border-str);
  border-radius: 10px;
  text-align: center;
}
.ap-stat-card__val { font-size: 17px; font-weight: 900; color: var(--txt); line-height: 1; }
.ap-stat-card__lbl { font-size: 9.5px; color: var(--txt-3); font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; }

/* Precio */
.ap-precio {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: var(--primary-light);
  border: 1px solid var(--primary-dim);
  border-radius: 10px;
}
.ap-precio__label { font-size: 9px; font-weight: 800; letter-spacing: 0.1em; color: var(--primary-dark); text-transform: uppercase; opacity: 0.6; }
.ap-precio__val   { font-size: 13px; font-weight: 800; color: var(--primary-dark); }

/* Secciones genéricas */
.ap-section { display: flex; flex-direction: column; gap: 8px; }
.ap-section__label {
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.1em;
  color: var(--txt-4);
  text-transform: uppercase;
}

/* Amenidades */
.ap-amenidades { display: flex; flex-wrap: wrap; gap: 5px; }
.ap-amen {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 8px;
  background: #ffffff;
  border: 1px solid var(--border-str);
  font-size: 11.5px;
  color: var(--txt-2);
  transition: border-color 0.12s, background 0.12s;
}
.ap-amen:hover { border-color: var(--primary-dim); background: var(--primary-light); }
.ap-amen__icon { font-size: 13px; }
.ap-amen__label { font-size: 11px; }

/* Host */
.ap-host {
  display: flex;
  align-items: center;
  gap: 11px;
  padding: 10px;
  background: var(--bg-1);
  border: 1px solid var(--border-str);
  border-radius: 10px;
}
.ap-host__avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  flex-shrink: 0;
  background: linear-gradient(135deg, #FC7010 0%, #B91E7C 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 800;
  color: #ffffff;
}
.ap-host__info { flex: 1; min-width: 0; }
.ap-host__name { font-size: 13px; font-weight: 700; color: var(--txt); margin-bottom: 2px; }
.ap-host__meta { font-size: 11.5px; color: var(--txt-3); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

/* ══ ESTADOS ═══════════════════════════════════════ */
.ap-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: var(--txt-4);
  font-size: 13px;
  padding: 24px;
}
.ap-state--error { color: var(--red); }

.ap-spinner {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2.5px solid #ECEAE4;
  border-top-color: var(--primary);
  animation: ap-spin 0.75s linear infinite;
}
@keyframes ap-spin { to { transform: rotate(360deg); } }

/* ══ TRANSICIONES ══════════════════════════════════ */
.ap-fade-enter-active,
.ap-fade-leave-active { transition: opacity 0.18s ease, transform 0.18s ease; }
.ap-fade-enter { opacity: 0; transform: scale(0.97) translateY(6px); }
.ap-fade-leave-to   { opacity: 0; transform: scale(0.97) translateY(6px); }

.ap-fade-q-enter-active,
.ap-fade-q-leave-active { transition: opacity 0.1s; }
.ap-fade-q-enter,
.ap-fade-q-leave-to { opacity: 0; }

.ap-slide-enter-active,
.ap-slide-leave-active { transition: opacity 0.15s ease, transform 0.15s ease; }
.ap-slide-enter { opacity: 0; transform: translateX(8px); }
.ap-slide-leave-to   { opacity: 0; transform: translateX(-8px); }

* { scrollbar-width: thin; scrollbar-color: #ECEAE4 transparent; }

/* ═══════════════════════════════════════════════════════════════
   RESPONSIVE — el popup flotante pasa a pantalla completa en móvil
   (sobre-escribe el left/top/width/height inline con !important)
════════════════════════════════════════════════════════════════ */
@media (max-width: 640px) {
  .ap-popup {
    left: 0 !important;
    top: 0 !important;
    width: 100vw !important;
    height: 100vh !important;
    height: 100dvh !important;
    max-width: 100vw;
    border-radius: 0 !important;
  }
  /* Sin arrastrar ni redimensionar con el dedo */
  .ap-resize { display: none !important; }
  .ap-header { cursor: default; }
}
</style>